import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

/**
 * Custom hook for subscription management
 * Provides subscription data, usage tracking, and limit checking
 */
export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch subscription and usage data
  const fetchSubscription = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch subscription and usage in parallel
      const [subRes, usageRes] = await Promise.all([
        axios.get(`${API_URL}/subscriptions/${user.id}`).catch(() => null),
        axios.get(`${API_URL}/subscriptions/${user.id}/usage`).catch(() => null)
      ]);

      if (subRes?.data?.subscription) {
        setSubscription(subRes.data.subscription);
      } else {
        // Default to free tier if no subscription
        setSubscription({ tier: 'free', status: 'active' });
      }

      if (usageRes?.data) {
        setUsage(usageRes.data);
      } else {
        // Default usage for free tier
        setUsage({
          tier: 'free',
          status: 'active',
          usage: {
            messages: { current: 0, limit: 2, remaining: 2, percentage: 0 },
            recipients: { current: 0, limit: 2, remaining: 2, percentage: 0 },
            whatsapp: { current: 0, limit: 2, remaining: 2, percentage: 0 },
            podcast_sessions: { current: 0, limit: 0, remaining: 0, percentage: 0 }
          }
        });
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  /**
   * Check if user can perform an action based on limits
   * @param {string} limitType - Type of limit (messages, recipients, whatsapp, podcast_sessions)
   * @returns {Promise<{allowed: boolean, current: number, limit: number, remaining: number}>}
   */
  const checkLimit = useCallback(async (limitType) => {
    if (!user?.id) {
      return { allowed: false, current: 0, limit: 0, remaining: 0, message: 'Not authenticated' };
    }

    try {
      const response = await axios.post(`${API_URL}/subscriptions/check-limit`, {
        user_id: user.id,
        usage_type: limitType
      });

      return response.data;
    } catch (err) {
      console.error('Failed to check limit:', err);
      
      // If API fails, check against local usage data
      if (usage?.usage?.[limitType]) {
        const usageData = usage.usage[limitType];
        const allowed = usageData.limit === -1 || usageData.current < usageData.limit;
        return {
          allowed,
          current: usageData.current,
          limit: usageData.limit,
          remaining: usageData.remaining,
          message: allowed ? 'OK' : 'Limit reached'
        };
      }
      
      // If no local data, allow by default (fail open, not closed)
      return { allowed: true, current: 0, limit: 2, remaining: 2, message: 'Using fallback' };
    }
  }, [user, usage]);

  /**
   * Increment usage counter after successful action
   * @param {string} usageType - Type of usage to increment
   * @param {number} amount - Amount to increment (default 1)
   */
  const incrementUsage = useCallback(async (usageType, amount = 1) => {
    if (!user?.id) return false;

    try {
      await axios.post(`${API_URL}/subscriptions/increment-usage`, {
        user_id: user.id,
        usage_type: usageType,
        amount
      });

      // Refresh usage data
      await fetchSubscription();
      return true;
    } catch (err) {
      console.error('Failed to increment usage:', err);
      return false;
    }
  }, [user, fetchSubscription]);

  /**
   * Upgrade subscription to a higher tier
   * @param {string} targetTier - Target tier (starter, premium, lifetime)
   */
  const upgradeSubscription = useCallback(async (targetTier) => {
    if (!user?.id) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await axios.post(`${API_URL}/subscriptions/upgrade`, {
        user_id: user.id,
        target_tier: targetTier
      });

      // Refresh subscription data
      await fetchSubscription();
      return response.data;
    } catch (err) {
      console.error('Failed to upgrade subscription:', err);
      throw err;
    }
  }, [user, fetchSubscription]);

  /**
   * Check if user has reached or exceeded a limit
   * @param {string} limitType - Type of limit to check
   * @returns {boolean}
   */
  const hasReachedLimit = useCallback((limitType) => {
    if (!usage?.usage?.[limitType]) return false;
    
    const { current, limit } = usage.usage[limitType];
    
    // -1 means unlimited
    if (limit === -1) return false;
    
    return current >= limit;
  }, [usage]);

  /**
   * Get usage percentage for a specific limit type
   * @param {string} limitType - Type of limit
   * @returns {number} Percentage (0-100)
   */
  const getUsagePercentage = useCallback((limitType) => {
    if (!usage?.usage?.[limitType]) return 0;
    
    const { current, limit } = usage.usage[limitType];
    
    if (limit === -1) return 0; // Unlimited
    if (limit === 0) return 100; // No quota
    
    return Math.min(100, Math.round((current / limit) * 100));
  }, [usage]);

  return {
    subscription,
    usage,
    loading,
    error,
    checkLimit,
    incrementUsage,
    upgradeSubscription,
    hasReachedLimit,
    getUsagePercentage,
    refreshSubscription: fetchSubscription
  };
}
